import { useState } from 'react'
import { Link } from 'react-router-dom'
import { TopBar } from '../components/TopBar'
import { Sheet } from '../components/Sheet'
import { Pill } from '../components/Pill'
import { Avatar, roleLabels } from '../components/Avatar'
import { useApp } from '../store/AppContext'
import { handoverSummaryLine, isStructuredHandover } from '../lib/handover'
import { shiftSummary } from '../lib/cover'
import type { UpdatePost, UpdateType, User } from '../types'

type TeamUpdateType = Exclude<UpdateType, 'shopping'>

const typeMeta: Record<TeamUpdateType, { label: string; icon: string; style: string }> = {
  handover: { label: 'Handover', icon: '🔄', style: 'bg-brand-100 text-brand-700' },
  instruction: { label: 'Instruction', icon: '📌', style: 'bg-blue-100 text-blue-700' },
  general: { label: 'General', icon: '💬', style: 'bg-slate-100 text-slate-600' },
}

function timeAgo(iso: string) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

export function Updates() {
  const { visibleUpdates, allUsers, addUpdate, can, canWriteHandover, currentUser } = useApp()
  const [filter, setFilter] = useState<'all' | TeamUpdateType>('all')
  const [showAdd, setShowAdd] = useState(false)
  const canSeeHandovers = can('view_handover')
  const showShoppingPrompt = !['coordinator', 'allied_health'].includes(currentUser.role)
  const filterTypes = (Object.keys(typeMeta) as TeamUpdateType[]).filter((type) => type !== 'handover' || canSeeHandovers)
  const teamUpdates = visibleUpdates.filter((update): update is UpdatePost & { type: TeamUpdateType } => update.type !== 'shopping')
  const filtered = teamUpdates.filter((update) => filter === 'all' || update.type === filter)
  const taggedForMe = teamUpdates.filter((update) => update.taggedUserIds?.includes(currentUser.id))

  return (
    <div>
      <TopBar title="Team Updates" subtitle="Handovers, notes, wins & important information" />
      <div className="no-scrollbar flex gap-2 overflow-x-auto border-b border-slate-100 bg-white px-4 py-3">
        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} label="All" />
        {filterTypes.map((type) => <FilterChip key={type} active={filter === type} onClick={() => setFilter(type)} label={`${typeMeta[type].icon} ${typeMeta[type].label}`} />)}
      </div>

      {taggedForMe.length > 0 && (
        <div className="px-4 pt-4">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3.5">
            <p className="text-sm font-bold text-amber-900">🔔 You were tagged in {taggedForMe.length} update{taggedForMe.length === 1 ? '' : 's'}</p>
            <p className="mt-1 text-xs text-amber-700">Tagged updates notify you inside Marnixia instead of sending a text message.</p>
          </div>
        </div>
      )}

      {showShoppingPrompt && (
        <div className="px-4 pt-4">
          <Link to="/shopping" className="flex items-center gap-3 rounded-2xl border border-teal-200 bg-teal-50 p-3.5">
            <span className="text-xl">🛒</span>
            <div className="flex-1"><p className="text-sm font-bold text-teal-900">Shopping has its own list</p><p className="text-xs text-teal-700">Open the dedicated Shopping List so it is easy to find and use.</p></div>
            <span className="text-teal-600">→</span>
          </Link>
        </div>
      )}

      {canWriteHandover && (
        <div className="px-4 pt-3">
          <Link to="/handover" className="flex items-center gap-3 rounded-2xl border border-brand-200 bg-brand-50 p-3.5">
            <span className="text-xl">📝</span><div className="flex-1"><p className="text-sm font-bold text-brand-800">Write a shift handover</p><p className="text-xs text-brand-700/80">Your shift record will appear here for authorised readers.</p></div><span className="text-brand-600">›</span>
          </Link>
        </div>
      )}

      <div className="flex flex-col gap-2 px-4 py-4">
        {filtered.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400">Nothing here yet.</div>}
        {filtered.map((update) => {
          const author = allUsers.find((user) => user.id === update.authorId)
          if (!author) return null
          return <UpdateCard key={update.id} update={update} author={author} />
        })}
      </div>

      <div className="pointer-events-none fixed inset-x-0 bottom-24 z-20"><div className="relative mx-auto max-w-md"><button onClick={() => setShowAdd(true)} className="pointer-events-auto absolute right-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-600 text-2xl text-white shadow-xl">+</button></div></div>
      <Sheet open={showAdd} onClose={() => setShowAdd(false)} title="Post an update"><AddUpdateForm onClose={() => setShowAdd(false)} onSave={(update) => { addUpdate(update); setShowAdd(false) }} /></Sheet>
    </div>
  )
}

function UpdateCard({ update, author }: { update: UpdatePost & { type: TeamUpdateType }; author: User }) {
  const { eventById, allUsers, currentUser } = useApp()
  const meta = typeMeta[update.type]
  const shift = update.linkedEventId ? eventById(update.linkedEventId) : undefined
  const text = isStructuredHandover(update) ? handoverSummaryLine(update) : update.text
  const taggedUsers = (update.taggedUserIds ?? []).map((id) => allUsers.find((user) => user.id === id)).filter((user): user is User => !!user)
  const taggedMe = update.taggedUserIds?.includes(currentUser.id)

  return (
    <div className={`rounded-2xl border bg-white p-3.5 shadow-card ${taggedMe ? 'border-amber-300' : 'border-slate-200'}`}>
      <div className="flex items-start gap-3">
        <Avatar user={author} size={34} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2"><p className="text-xs font-semibold text-slate-900">{author.name} <span className="font-normal text-slate-400">· {roleLabels[author.role]}</span></p><Pill className={meta.style}>{meta.icon} {meta.label}</Pill></div>
          {taggedMe && <p className="mt-2 inline-flex rounded-full bg-amber-100 px-2 py-1 text-[11px] font-bold text-amber-800">🔔 Tagged you</p>}
          {shift && <div className="mt-2"><Pill className="bg-slate-100 text-slate-600">👥 {shiftSummary(shift)}</Pill></div>}
          <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{text}</p>
          {taggedUsers.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {taggedUsers.map((user) => <span key={user.id} className="rounded-full bg-brand-50 px-2 py-1 text-[11px] font-semibold text-brand-700">@{user.name}</span>)}
            </div>
          )}
          {isStructuredHandover(update) && update.handover && (
            <div className="mt-2 grid gap-1.5">
              {update.handover.followUp?.trim() && <p className="rounded-xl bg-amber-50 p-2 text-xs text-amber-900"><strong>Follow-up:</strong> {update.handover.followUp}</p>}
              {update.handover.concerns?.trim() && <p className="rounded-xl bg-rose-50 p-2 text-xs text-rose-900"><strong>Concerns:</strong> {update.handover.concerns}</p>}
            </div>
          )}
          <p className="mt-2 text-[11px] text-slate-400">{timeAgo(update.createdAt)}</p>
        </div>
      </div>
    </div>
  )
}

function FilterChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return <button onClick={onClick} className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${active ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-500'}`}>{label}</button>
}

function AddUpdateForm({ onClose, onSave }: { onClose: () => void; onSave: (update: UpdatePost) => void }) {
  const { currentUser, canWriteHandover, allUsers } = useApp()
  const canPostInstruction = currentUser.role === 'allied_health' || currentUser.role === 'coordinator'
  const showShoppingPrompt = !['coordinator', 'allied_health'].includes(currentUser.role)
  const availableTypes: TeamUpdateType[] = canPostInstruction ? ['general', 'instruction'] : ['general']
  const [type, setType] = useState<TeamUpdateType>('general')
  const [text, setText] = useState('')
  const [taggedUserIds, setTaggedUserIds] = useState<string[]>([])
  const taggableUsers = allUsers.filter((user) => user.id !== currentUser.id)

  const toggleTag = (userId: string) => {
    setTaggedUserIds((current) => current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId])
  }

  const submit = () => {
    if (!text.trim()) return
    onSave({ id: `up-${Date.now()}`, authorId: currentUser.id, type, text: text.trim(), createdAt: new Date().toISOString(), notifyBySms: false, taggedUserIds })
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">{availableTypes.map((value) => <button key={value} onClick={() => setType(value)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${type === value ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-500'}`}>{typeMeta[value].icon} {typeMeta[value].label}</button>)}</div>
      {showShoppingPrompt && <Link to="/shopping" onClick={onClose} className="rounded-xl border border-teal-200 bg-teal-50 px-3 py-2.5 text-xs font-semibold text-teal-800">🛒 Need a shopping list? Open Shopping List →</Link>}
      {canWriteHandover && <Link to="/handover" onClick={onClose} className="rounded-xl border border-brand-200 bg-brand-50 px-3 py-2.5 text-xs font-semibold text-brand-700">🔄 Writing up a shift? Use Shift Handover →</Link>}
      <label className="block text-xs font-semibold text-slate-600"><span className="mb-1 block">Message</span><textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Write your update..." className="input" /></label>

      <div>
        <p className="text-xs font-semibold text-slate-600">Tag someone for an in-app notification</p>
        <p className="mt-0.5 text-[11px] text-slate-400">Only the people you select are specifically alerted.</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {taggableUsers.map((user) => {
            const selected = taggedUserIds.includes(user.id)
            return (
              <button key={user.id} type="button" onClick={() => toggleTag(user.id)} className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-xs font-semibold ${selected ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-600'}`}>
                <Avatar user={user} size={20} /> @{user.name}
              </button>
            )
          })}
        </div>
      </div>

      <div className="mt-2 flex gap-2"><button onClick={onClose} className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-600">Cancel</button><button onClick={submit} className="flex-1 rounded-xl bg-brand-600 py-3 text-sm font-bold text-white">Post Update</button></div>
    </div>
  )
}

